import React, { useState, useEffect, useRef, useMemo } from 'react';
import { RawShopEntry } from '../../utils/fortniteShopParser';
import { getSectionColor } from './CatalogGrid';

export interface FloatingShopControlsProps {
  /** Ordered shop sections tuple [sectionName, entries] */
  sections: [string, RawShopEntry[]][];
  /** Current search query */
  searchQuery: string;
  /** Search query callback */
  onSearchChange: (query: string) => void;
  /** Active section index (optional controlled) */
  activeSectionIdx?: number;
  /** Callback when user selects a section from the index dropdown */
  onSelectSection?: (index: number) => void;
  /** Total matching items count (optional) */
  totalMatchingItems?: number;
  /** Optional extra classes */
  className?: string;
}

export const FloatingShopControls: React.FC<FloatingShopControlsProps> = ({
  sections,
  searchQuery,
  onSearchChange,
  activeSectionIdx: controlledActiveIdx,
  onSelectSection,
  className = '',
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [internalActiveIdx, setInternalActiveIdx] = useState(0);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const desktopMenuRef = useRef<HTMLDivElement>(null);
  const mobileContainerRef = useRef<HTMLDivElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const desktopSearchInputRef = useRef<HTMLInputElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const activeIdx = controlledActiveIdx !== undefined ? controlledActiveIdx : internalActiveIdx;
  const currentSection = sections[activeIdx] || sections[0];
  const currentSectionName = currentSection ? currentSection[0] : 'Tienda';

  // Dynamic Theme Color resolution for the active section (strictly synchronized 1:1 with CatalogGrid)
  const activeSectionColor = useMemo(() => {
    if (!currentSection) return '#1447e6';
    const [, entries] = currentSection;
    return getSectionColor(entries);
  }, [currentSection]);

  // Live detection of visible section and global scroll progress of the entire web page (60-120fps hardware-accelerated)
  useEffect(() => {
    let ticking = false;
    let sectionEls: HTMLElement[] = [];

    const querySectionEls = () => {
      sectionEls = [];
      for (let i = 0; i < sections.length; i++) {
        const el = document.getElementById(`section-${i}`);
        if (el) sectionEls.push(el);
      }
    };

    querySectionEls();

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // 1. Detect active section for title and section theme color
          if (sectionEls.length > 0) {
            const scrollOffset = 150;
            let bestIdx = 0;
            let minDistance = Infinity;

            for (let i = 0; i < sectionEls.length; i++) {
              const rect = sectionEls[i].getBoundingClientRect();
              const dist = Math.abs(rect.top - scrollOffset);
              if (rect.top <= scrollOffset + 120 && dist < minDistance) {
                minDistance = dist;
                bestIdx = i;
              }
            }

            if (controlledActiveIdx === undefined) {
              setInternalActiveIdx((prev) => (prev !== bestIdx ? bestIdx : prev));
            }
          }

          // 2. Compute total scroll progress of the entire web page (0% at top to 100% at bottom)
          const docElem = document.documentElement;
          const totalHeight = docElem.scrollHeight - window.innerHeight;
          const currentScroll = window.pageYOffset || docElem.scrollTop || 0;
          const globalProgress = totalHeight > 0 ? (currentScroll / totalHeight) * 100 : 0;
          const clamped = Math.min(100, Math.max(0, globalProgress));

          // Direct DOM transform without React render loop thrashing
          if (progressBarRef.current) {
            progressBarRef.current.style.width = `${clamped}%`;
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', () => {
      querySectionEls();
      handleScroll();
    }, { passive: true });

    // Run once on mount to initialize progress
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [sections, controlledActiveIdx]);

  // Desktop Menu Hover Handlers
  const handleDesktopMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsDesktopMenuOpen(true);
  };

  const handleDesktopMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsDesktopMenuOpen(false);
    }, 220);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Close menus on outside click or escape
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (desktopMenuRef.current && !desktopMenuRef.current.contains(target)) {
        setIsDesktopMenuOpen(false);
      }
      if (mobileContainerRef.current && !mobileContainerRef.current.contains(target)) {
        setIsMobileMenuOpen(false);
        if (isSearchFocused) {
          mobileSearchInputRef.current?.blur();
          setIsSearchFocused(false);
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDesktopMenuOpen(false);
        setIsMobileMenuOpen(false);
        if (isSearchFocused) {
          mobileSearchInputRef.current?.blur();
          setIsSearchFocused(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Jump to section handler
  const handleJumpToSection = (idx: number) => {
    setIsMobileMenuOpen(false);
    setIsDesktopMenuOpen(false);

    if (controlledActiveIdx === undefined) {
      setInternalActiveIdx(idx);
    }

    if (onSelectSection) {
      onSelectSection(idx);
    } else {
      const el = document.getElementById(`section-${idx}`);
      if (el) {
        const yOffset = -100;
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      {/* =========================================================================
          1. MÓVIL: BARRA FLOTANTE CON COLUMNAS DINÁMICAS (< lg)
          ========================================================================= */}
      <div
        ref={mobileContainerRef}
        className={`lg:hidden sticky top-14 sm:top-16 z-40 w-full flex justify-center px-3 py-2 pointer-events-none select-none ${className}`}
      >
        <div className="relative w-full max-w-md pointer-events-auto filter drop-shadow-[4px_4px_0_#1447e6]">
          {/* Card with dynamic column proportions, left accent strip and bottom progress bar */}
          <div
            className="bg-white text-[#06173f] border-t-2 border-r-2 border-[#cfd7e3] h-11 sm:h-12 flex divide-x-2 divide-[#cfd7e3] overflow-hidden relative"
            style={{ clipPath: 'var(--g-cut)' }}
          >
            {/* Dynamic Section Theme Color Left Accent Strip (Image 2 style) */}
            <div
              className="absolute left-0 top-0 bottom-0 pointer-events-none transition-colors duration-300 z-10"
              style={{
                width: '7px',
                backgroundColor: activeSectionColor,
              }}
            />

            {/* Dynamic Section Progress Bar (Directly glued to the bottom blue shadow line, no gap, no 3 lines, crisp with no shadow/glow) */}
            <div
              ref={progressBarRef}
              className="absolute bottom-0 left-0 h-1 pointer-events-none will-change-[width] z-10"
              style={{
                width: '0%',
                backgroundColor: activeSectionColor,
                transition: 'background-color 0.3s ease',
              }}
            />

            {/* Columna Izquierda: Índice (Más ancha en reposo ~70%, se contrae suavemente a ~24% cuando el buscador se enfoca) */}
            <div
              className={`h-full overflow-hidden transition-[width] duration-300 ease-out flex items-center ${
                isSearchFocused ? 'w-[24%] sm:w-[26%]' : 'w-[70%] sm:w-[72%]'
              }`}
            >
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="w-full h-full pl-4 sm:pl-4.5 pr-2 sm:pr-2.5 flex items-center justify-between gap-1.5 hover:bg-[#f2f4f7] cursor-pointer text-left truncate transition-colors select-none"
                title="Índice de Secciones"
                aria-expanded={isMobileMenuOpen}
                aria-label="Abrir selector de secciones"
              >
                <span className="font-chakra font-black italic uppercase text-[13.5px] sm:text-[15px] text-[#06173f] tracking-tight truncate translate-y-[1px]">
                  {currentSectionName}
                </span>
                <i
                  className={`fa-solid fa-chevron-down text-xs text-[#1447e6] shrink-0 transition-transform duration-200 ${
                    isMobileMenuOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </div>

            {/* Columna Derecha: Buscador (Se expande suavemente hacia la izquierda ~76% al enfocar o escribir, ~30% en reposo) */}
            <div
              className={`h-full overflow-hidden transition-[width] duration-300 ease-out flex items-center relative bg-white ${
                isSearchFocused ? 'w-[76%] sm:w-[74%]' : 'w-[30%] sm:w-[28%]'
              }`}
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  mobileSearchInputRef.current?.blur();
                }}
                className="w-full h-full px-2.5 sm:px-3 flex items-center gap-1.5 sm:gap-2"
              >
                <i className="fa-solid fa-magnifying-glass text-[#1447e6] text-xs sm:text-[13px] shrink-0" />
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onFocus={() => {
                    setIsSearchFocused(true);
                    setIsMobileMenuOpen(false);
                  }}
                  onBlur={() => setIsSearchFocused(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Escape') {
                      mobileSearchInputRef.current?.blur();
                    }
                  }}
                  enterKeyHint="search"
                  placeholder="Buscar..."
                  aria-label="Buscar cosméticos"
                  className="w-full bg-transparent text-sm sm:text-base font-sans font-semibold text-[#06173f] placeholder:text-slate-400 focus:outline-none truncate"
                />
                {searchQuery.length > 0 && (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSearchChange('');
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      onSearchChange('');
                    }}
                    onClick={() => onSearchChange('')}
                    aria-label="Limpiar búsqueda"
                    className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-[#06173f] rounded cursor-pointer shrink-0"
                  >
                    <i className="fa-solid fa-xmark text-xs" />
                  </button>
                )}
              </form>
            </div>
          </div>

          {/* Menú Desplegable Móvil */}
          {isMobileMenuOpen && (
            <div
              className="absolute top-full left-0 right-0 mt-2 bg-white text-[#06173f] border-2 border-[#cfd7e3] shadow-2xl overflow-hidden z-50 animate-fade-in"
              style={{ clipPath: 'var(--g-cut)' }}
            >
              <div className="max-h-64 overflow-y-auto p-1.5 space-y-1 custom-scrollbar">
                {sections.map(([name, items], idx) => {
                  const isSelected = idx === activeIdx;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => handleJumpToSection(idx)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left text-xs font-chakra font-black uppercase italic tracking-tight transition-colors cursor-pointer rounded ${
                        isSelected
                          ? 'bg-[#1447e6] text-white'
                          : 'bg-[#f2f4f7] text-[#06173f] hover:bg-[#e2e6eb]'
                      }`}
                    >
                      <span className="truncate">{name}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded font-bold shrink-0 ml-2 ${
                          isSelected ? 'bg-white/20 text-white' : 'bg-white text-[#1447e6]'
                        }`}
                      >
                        {items.length}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* =========================================================================
          2. ESCRITORIO: DOCK VERTICAL AL ESTILO OFICIAL FORTNITE EPIC (>= lg)
          ========================================================================= */}
      <div
        ref={desktopMenuRef}
        onMouseEnter={handleDesktopMouseEnter}
        onMouseLeave={handleDesktopMouseLeave}
        className="hidden lg:flex fixed left-4 xl:left-8 top-24 z-40 select-none items-start"
      >
        {/* Cápsula Vertical: Botón Menú + Puntos de sección (sin lupa, sin barras de scroll) */}
        <div className="bg-[#0d1322]/90 backdrop-blur-md border border-white/10 rounded-full py-3 px-2 flex flex-col items-center gap-2.5 shadow-2xl">
          {/* Botón de Menú con 3 líneas (Abre el popover al hacer clic o al posar mouse) */}
          <button
            type="button"
            onClick={() => setIsDesktopMenuOpen(!isDesktopMenuOpen)}
            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all cursor-pointer ${
              isDesktopMenuOpen
                ? 'bg-white text-[#06173f] shadow-md scale-105'
                : 'bg-white/15 hover:bg-white/25 text-white'
            }`}
            title="Índice de Secciones"
            aria-expanded={isDesktopMenuOpen}
            aria-label="Abrir menú de secciones"
          >
            <i className="fa-solid fa-bars-staggered text-sm" />
          </button>

          {/* Línea divisoria sutil */}
          <div className="w-4 h-[1px] bg-white/15 my-0.5" />

          {/* Track vertical de puntos (SIN barra de navegación visible: no-scrollbar) */}
          <div className="flex flex-col items-center gap-2.5 py-1 max-h-[58vh] overflow-y-auto no-scrollbar">
            {sections.map(([name], idx) => {
              const isSelected = idx === activeIdx;

              if (isSelected) {
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleJumpToSection(idx)}
                    className="group relative flex items-center justify-center cursor-pointer"
                    aria-label={`Sección activa: ${name}`}
                  >
                    {/* Punto activo: cápsula blanca brillante elongada */}
                    <span className="w-2.5 h-4 bg-white rounded-full shadow-[0_0_10px_rgba(255,255,255,0.9)] transition-all" />
                    {/* Tooltip lateral */}
                    <span className="absolute left-6 px-2.5 py-1 bg-[#06173f] text-white text-[11px] font-chakra font-black uppercase italic whitespace-nowrap rounded-md border border-[#1447e6] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity shadow-lg z-50">
                      {name}
                    </span>
                  </button>
                );
              }

              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => handleJumpToSection(idx)}
                  className="group relative flex items-center justify-center cursor-pointer py-0.5"
                  aria-label={`Ir a sección: ${name}`}
                >
                  {/* Punto inactivo: círculo sutil */}
                  <span className="w-2 h-2 bg-white/35 group-hover:bg-white/80 group-hover:scale-125 rounded-full transition-all" />
                  {/* Tooltip lateral */}
                  <span className="absolute left-6 px-2.5 py-1 bg-[#06173f] text-white text-[11px] font-chakra font-black uppercase italic whitespace-nowrap rounded-md border border-[#1447e6] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity shadow-lg z-50">
                    {name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Panel lateral popover de secciones (SIN barra de navegación visible: no-scrollbar) */}
        {isDesktopMenuOpen && (
          <div className="ml-3 w-72 md:w-80 bg-[#0d1322]/95 backdrop-blur-xl border border-white/15 rounded-2xl p-3 shadow-2xl text-white max-h-[75vh] flex flex-col gap-2.5 animate-fade-in z-50">
            {/* Buscador integrado arriba de la lista */}
            <div className="relative flex items-center bg-white/10 rounded-xl px-3 py-2 border border-white/10 focus-within:border-[#5ccbff] focus-within:bg-white/15 transition-all">
              <i className="fa-solid fa-magnifying-glass text-[#5ccbff] text-xs mr-2" />
              <input
                ref={desktopSearchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Buscar cosméticos..."
                aria-label="Buscar en la tienda"
                className="w-full bg-transparent text-xs font-semibold text-white placeholder:text-slate-400 focus:outline-none truncate font-chakra"
              />
              {searchQuery.length > 0 && (
                <button
                  type="button"
                  onClick={() => onSearchChange('')}
                  aria-label="Limpiar búsqueda"
                  className="text-slate-400 hover:text-white text-xs cursor-pointer ml-1"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>

            {/* Lista vertical de Secciones (con no-scrollbar) */}
            <div className="overflow-y-auto space-y-1 no-scrollbar pr-1 max-h-[55vh]">
              {sections.map(([name, items], idx) => {
                const isSelected = idx === activeIdx;

                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleJumpToSection(idx)}
                    className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl font-chakra font-black uppercase text-xs italic tracking-tight transition-all cursor-pointer ${
                      isSelected
                        ? 'border-2 border-white bg-white/10 text-white shadow-[0_0_12px_rgba(255,255,255,0.15)]'
                        : 'border border-transparent text-slate-300 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span className="truncate">{name}</span>
                    <span
                      className={`text-[10px] font-sans font-bold px-1.5 py-0.5 rounded ml-2 shrink-0 ${
                        isSelected ? 'bg-white/20 text-white' : 'text-slate-400'
                      }`}
                    >
                      {items.length}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FloatingShopControls;
